import { useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { wbsService } from '../services/wbsService';
import { WbsList } from './WbsList';

export const TaskManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    data: wbsResponse,
    isLoading: isLoadingWbs,
    error: wbsError,
    refetch: refetchWbs,
  } = useQuery({
    queryKey: ['wbs'],
    queryFn: () => wbsService.getWbs(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const wbsItems = wbsResponse || [];

  if (isLoadingWbs) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-lg bg-surface-card p-8 shadow-sm">
        <div className="flex flex-col items-center gap-4 text-text-secondary">
          <Spinner className="size-10" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (wbsError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg bg-surface-card p-8 text-danger-600 shadow-sm">
        <p>Error loading data: {wbsError instanceof Error ? wbsError.message : 'Unknown error'}</p>
        <Button type="button" onClick={() => void refetchWbs()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <WbsList
        wbsItems={wbsItems}
        isLoading={isLoadingWbs}
        onEdit={() => {
          queryClient.invalidateQueries({ queryKey: ['wbs'] });
        }}
        onDelete={async (wbsId) => {
          if (
            window.confirm(
              'Are you sure you want to delete this task? This action cannot be undone.'
            )
          ) {
            try {
              await wbsService.deleteWbs(wbsId);
              await refetchWbs();
            } catch (error) {
              alert(
                `Failed to delete task: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          }
        }}
        onAdd={() => {
          queryClient.invalidateQueries({ queryKey: ['wbs'] });
        }}
      />
    </div>
  );
};
